import {
  ComponentType,
  DashboardState,
  EScreenType,
  ESdChangeReason,
  IComponentDefinition,
  ICRUDFormDiMetadata,
  IDisplayItem,
  IDisplayItemComponent,
  RightPanelType,
  SpecialDIIds,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Resizable } from "re-resizable";
import { ReactNode, useEffect, useState } from "react";
import { connect } from "react-redux";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { ScreenService } from "../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IPureDesignComponentProps, PureSdComponent } from "../../../shared/pure-sd";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { duplicateDesignerScreen } from "../../../shared/utility/designer-screen-actions";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  clampRightWidth,
  CSS_VAR_RIGHT,
  getRightPanelMaxWidth,
  PANEL_MARGIN_BUFFER_PX,
  readLeftPanelWidth,
  readRightPanelWidth,
  RIGHT_MIN_WIDTH,
  setCssVarPx,
  supportsRightPanelResize
} from "../../../shared/utility/panel-dimensions";
import { PropertyHelper } from "../../../shared/utility/property-helper";
import { PropertySdHelper } from "../../../shared/utility/property-sd-helper";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { DeleteModal } from "../../common/delete-modal";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaComponentIcon from "../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  getActiveProcessEditorTab,
  subscribeActiveProcessEditorTab
} from "../flows/process-editor/process-editor-tab-bus";
import { isDiagramTab } from "../flows/process-editor/process-editor-tabs";
import McpChatDocked from "../left-menu/mcp-chat/panel/mcp-chat-docked";
import { CustomAIActionTemplateProperties } from "./datasource-panel/custom-ai-actions-properties";
import { DocumentsWindow } from "./documents-panel/documents-window";
import "./right-panel.scss";
import { PropertiesWindow } from "./properties-panel/properties-window";
import { ZendeskProperties } from "./properties-panel/zendesk-properties";
import RightPanelHeader from "./right-panel-header/right-panel-header";
import { RightPanelItemNameComponent } from "./right-panel-item-name-component";
import { StylingWindow } from "./styling-panel/styling-window";
import { WorkflowWindow } from "./workflow-panel/workflow-window";
export interface IDesignRightPanelState {
  isRenaming: boolean;
  renamingId: string;
  triggerScreenDeleteModal: boolean;
  screenToDelete: IScreenDesignContextState;
  dropdownVisible: boolean;
  isRenameLoading: boolean;
  multiSelectCount: number;
  panelWidth: number;
  isResizing: boolean;
}

export interface IDesignRightPanelProps {
  showDomainAccessRequiredModal: (accessName: string | string[]) => void;
}

interface IWorkflowRightPanelTabGateProps {
  isWorkflowPanelActive: boolean;
  children: ReactNode;
}

/**
 * The right-panel shell is shared by every designer mode. In workflow mode it *is* the
 * Diagram tab's property inspector, so off the Diagram tab (Parameters, Forms, …) it would
 * render an empty frame — those tabs own the full-width content column. This gate drops the
 * whole shell in that one case, letting the workflow canvas reclaim the width; every other
 * mode (`isWorkflowPanelActive` false) renders unconditionally, so screen design and the
 * rest are untouched. It's a function component so its tab-bus subscription re-renders it
 * independently of the class's checkNeedRender; an undefined tab (no editor mounted yet)
 * counts as Diagram so the panel shows by default.
 */
const WorkflowRightPanelTabGate = ({
  isWorkflowPanelActive,
  children
}: IWorkflowRightPanelTabGateProps): JSX.Element | null => {
  const [tab, setTab] = useState<string | undefined>(getActiveProcessEditorTab());
  useEffect((): (() => void) => {
    setTab(getActiveProcessEditorTab());
    return subscribeActiveProcessEditorTab(setTab);
  }, []);

  if (!isWorkflowPanelActive) {
    return <>{children}</>;
  }

  const showPanel: boolean = tab === undefined || isDiagramTab(tab);
  return showPanel ? <>{children}</> : null;
};

class DesignRightPanel extends PureSdComponent<
  IDesignRightPanelProps & IWithSDProps & IWithContextProp,
  IDesignRightPanelState
> {
  propertyHelper: PropertyHelper;

  propertySdHelper: PropertySdHelper;

  activeParameterPropertyName: string = "";

  clipboardValue = "";

  private defaultPanelWidth = RIGHT_MIN_WIDTH;

  constructor(props) {
    super(props);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
    this.propertyHelper = new PropertyHelper();

    const sVar = {
      isRenaming: false,
      renamingId: "",
      triggerScreenDeleteModal: false,
      screenToDelete: {} as IScreenDesignContextState,
      dropdownVisible: false,
      isRenameLoading: false,
      multiSelectCount: 0,
      panelWidth: this.defaultPanelWidth,
      isResizing: false
    };
    this.state = sVar;
  }

  componentDidMount(): void {
    this.updateClipboardValue();
    this.startMultiSelectPolling();

    const savedWidth = localStorage.getItem("kuika_rightPanelWidth");
    let initialWidth = readRightPanelWidth();

    if (savedWidth) {
      const parsed = parseInt(savedWidth, 10);
      if (!isNaN(parsed)) {
        initialWidth = parsed;
      }
    }

    this.setState({ panelWidth: initialWidth });
    setCssVarPx(CSS_VAR_RIGHT, initialWidth);
  }

  componentWillUnmount(): void {
    this.stopMultiSelectPolling();
  }

  private multiSelectPollingInterval: NodeJS.Timeout | null = null;

  startMultiSelectPolling = () => {
    this.multiSelectPollingInterval = setInterval(() => {
      const multiSelectInfo = this.getMultiSelectInfo();
      if (multiSelectInfo && multiSelectInfo.count !== this.state.multiSelectCount) {
        this.setState({ multiSelectCount: multiSelectInfo.count });
      }
    }, 100);
  };

  stopMultiSelectPolling = () => {
    if (this.multiSelectPollingInterval) {
      clearInterval(this.multiSelectPollingInterval);
      this.multiSelectPollingInterval = null;
    }
  };

  getDiFormComponentOrOneOfItsParentIsForm = (di: IDisplayItem): IDisplayItem | undefined => {
    if (di?.item?.name === "Form") {
      return di;
    }

    if (di?.id === SpecialDIIds.rootDI || di?.parentId === SpecialDIIds.rootDI) {
      return undefined;
    }

    const parentDi = di?.parentId ? this.props.sd.screenDesignDictionary[di.parentId] : undefined;

    if (!parentDi) {
      return undefined;
    }

    return this.getDiFormComponentOrOneOfItsParentIsForm(parentDi);
  };

  private selectFormIfSelectedDiIsChildOfForm = () => {
    const selectedDi: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    if (!selectedDi) return;

    const formDi: IDisplayItem | undefined = this.getDiFormComponentOrOneOfItsParentIsForm(selectedDi);

    if (!formDi) return;

    const metadataProperty = formDi.item.properties.find((p) => p.name === "metadata");

    if (!metadataProperty) return;

    const metadata: ICRUDFormDiMetadata = JSON.parse(metadataProperty.symbol.fixedValue);

    if (!metadata) return;

    const allPredefinedIds: number[] = [];

    if (metadata.predefinedFormFooterIds) {
      if (metadata.predefinedFormFooterIds.footerColKuikaId) {
        allPredefinedIds.push(metadata.predefinedFormFooterIds.footerColKuikaId);
      }

      if (metadata.predefinedFormFooterIds.footerRowKuikaId) {
        allPredefinedIds.push(metadata.predefinedFormFooterIds.footerRowKuikaId);
      }

      if (metadata.predefinedFormFooterIds.footerButtonKuikaId) {
        allPredefinedIds.push(metadata.predefinedFormFooterIds.footerButtonKuikaId);
      }

      if (metadata.predefinedFormFooterIds.footerHorizontalStackKuikaId) {
        allPredefinedIds.push(metadata.predefinedFormFooterIds.footerHorizontalStackKuikaId);
      }

      if (metadata.predefinedFormFooterIds.footerUserInteractionButtonsKuikaIds) {
        allPredefinedIds.push(...Object.values(metadata.predefinedFormFooterIds.footerUserInteractionButtonsKuikaIds));
      }
    }

    metadata?.sections?.forEach((s) => {
      if (!s.predefinedIds) return;

      allPredefinedIds.push(
        s.predefinedIds.sectionKuikaId,
        s.predefinedIds.sectionHeaderRowKuikaId,
        s.predefinedIds.sectionTitleCellKuikaId,
        s.predefinedIds.sectionTitleKuikaId
      );

      s?.items?.forEach((i) => {
        if (!i.predefinedIds) return;

        allPredefinedIds.push(
          i.predefinedIds.fieldCellKuikaId,
          i.predefinedIds.inputKuikaId,
          i.predefinedIds.labelKuikaId
        );

        if (!i.accompanyingDisplayItemPredefinedIds) return;

        allPredefinedIds.push(...Object.values(i.accompanyingDisplayItemPredefinedIds));
      });
    });

    if (allPredefinedIds.filter(Boolean).includes(selectedDi.id)) return;

    this.props.sd.setSelectedDisplayItem(formDi.id.toString());
  };

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & IDesignRightPanelProps & IWithContextProp>,
    prevState: Readonly<IDesignRightPanelState>
  ): void {
    const prevIsDesignState =
      prevProps.sd.dashboardState === DashboardState.design ||
      prevProps.sd.dashboardState === DashboardState.reportDesigner;
    const currIsDesignState =
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.reportDesigner;

    if (prevProps.sd.dashboardState !== this.props.sd.dashboardState && (prevIsDesignState || currIsDesignState)) {
      this.setState({ panelWidth: this.defaultPanelWidth });
      setCssVarPx(CSS_VAR_RIGHT, this.defaultPanelWidth);
    }

    if (this.props.sd.selectedKuikaIds[0] !== prevProps.sd.selectedKuikaIds[0]) {
      this.setState({ isRenaming: false, renamingId: "" });
      if (prevState.renamingId === this.props.sd.renamingDisplayItemId?.toString()) {
        this.props.sd.setRenamingDisplayItemId();
      }
      this.selectFormIfSelectedDiIsChildOfForm();
      this.updateClipboardValue();
    }
  }

  handleResizeStart = () => {
    this.setState({ isResizing: true });
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  adjustCanvasIfNeeded = (newRightWidth: number): void => {
    const kuika_designArea = document.getElementById("kuika_designArea");
    const kuika_iframeWrapper = document.getElementsByClassName("kuika_iframeWrapper")[0] as HTMLElement;
    const kuika_iframeAgent_canvas = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;

    if (!kuika_designArea || !kuika_iframeWrapper) {
      return;
    }

    // See the left panel's copy: `screen.width` is not a report page's size, so this may only
    // run in the views whose panel is draggable in the first place.
    if (!supportsRightPanelResize(this.props.sd.dashboardState)) {
      return;
    }

    const leftPanelWidth = this.props.sd.isLeftPanelCollapsed?.[this.props.sd.dashboardState]
      ? 39
      : readLeftPanelWidth();

    const viewportWidth = window.innerWidth;
    const availableWrapperWidth = viewportWidth - leftPanelWidth - newRightWidth - PANEL_MARGIN_BUFFER_PX;

    const paddingBuffer = 24;
    const maxCanvasFitWidth = availableWrapperWidth - paddingBuffer;

    const screen = this.props.sd.project?.screenCanvasSizes?.screens?.find((s) => s.id === this.props.sd.sdId);
    const preferredWidth = screen?.width;

    if (preferredWidth) {
      const minCanvasWidth = 320;
      const newCanvasWidth = Math.max(minCanvasWidth, Math.min(preferredWidth, maxCanvasFitWidth));
      kuika_designArea.style.width = `${newCanvasWidth}px`;
      if (kuika_iframeAgent_canvas) {
        kuika_iframeAgent_canvas.style.width = `${newCanvasWidth}px`;
      }
    } else {
      kuika_designArea.style.width = `100%`;
      if (kuika_iframeAgent_canvas) {
        kuika_iframeAgent_canvas.style.width = `${maxCanvasFitWidth}px`;
      }
    }
  };

  handleResize = (e, direction, ref) => {
    const proposed = ref.offsetWidth;
    const newWidth = clampRightWidth(proposed, this.props.sd);
    if (newWidth === this.state.panelWidth) return;

    this.adjustCanvasIfNeeded(newWidth);

    this.setState({ panelWidth: newWidth });
    setCssVarPx(CSS_VAR_RIGHT, newWidth);
    localStorage.setItem("kuika_rightPanelWidth", newWidth.toString());

    const display =
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.reportDesigner;
    KMainFunctions.waitAndResize(display, this.props.sd.toolbarIsHidden, this.props.sd);
  };

  handleResizeStop = () => {
    this.setState({ isResizing: false });
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  handleResizeKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 50 : 10;
    let next = this.state.panelWidth;
    if (e.key === "ArrowLeft") next = this.state.panelWidth + step;
    if (e.key === "ArrowRight") next = this.state.panelWidth - step;
    const clamped = clampRightWidth(next);
    if (clamped !== this.state.panelWidth) {
      this.setState({ panelWidth: clamped });
      setCssVarPx(CSS_VAR_RIGHT, clamped);
    }
  };

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  getItemName = (isDisplay: boolean) => {
    const sd = this.props.sd.getScreenDesign();
    const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
    const component = this.props.sd.project?.componentSetObj?.components?.find((a) => a.id === di?.item?.id);
    const isDeprecated = component?.isDeprecated;
    if (isDeprecated && isDisplay) {
      return `(Deprecated) ${this.getDiFriendlyNameNameByKuikaId(this.props.sd.selectedKuikaIds[0])}`;
    }
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI && sd.name) {
      return sd.name;
    }
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      return this.props?.sd?.sdName;
    }
    return this.getDiFriendlyNameNameByKuikaId(this.props.sd.selectedKuikaIds[0]);
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getDiFriendlyNameNameByKuikaId = (kuikaId: string): string => {
    let result = kuikaId;
    if (kuikaId && kuikaId.length > 0) {
      const di = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];

      if (di?.friendlyName && di.friendlyName.length > 0) {
        result = di.friendlyName;
      }
    }
    return result;
  };

  renameSelectedItem = async (sd: IScreenDesignContextState) => {
    let renamingId = "";
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      renamingId = sd.sdId;
    } else {
      renamingId = this.props.sd.selectedKuikaIds[0];
      this.props.sd.setRenamingDisplayItemId(parseInt(renamingId, 10));
    }
    this.setState({ isRenaming: true, renamingId });
  };

  handleNameKeypress = async (e) => {
    try {
      if (e.keyCode === 13) {
        this.handleNameOnBlur(e);
      } else if (e.keyCode === 27) {
        this.setState({ isRenaming: false, renamingId: "" });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  changeDiFriendlyName = (newName: string) => {
    if (newName && newName.length > 0) {
      const sd = this.props.sd.getScreenDesign();
      const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
      if (di) {
        di.friendlyName = newName;
        this.setState({ isRenaming: false, renamingId: "" }, () => {
          this.props.sd.setScreenDesign(sd, ESdChangeReason.ChangeDiFriendlyName);
        });
      }
    }
  };

  handleNameOnBlur = async (e) => {
    let newName: any = e.target?.value ?? e.state.value;
    newName = newName.trim();
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      this.changeScreenName(newName);
    } else {
      if (
        ScreenDesignHelper.isDiFriendlyNameUnique(
          this.props.sd.getScreenDesign().displayItems,
          this.props.sd.selectedKuikaIds[0],
          newName
        ) === false
      ) {
        KMainFunctions.displayWarningNotification("This name is not unique. Please retry with another name.");
        this.setState({ isRenaming: false, renamingId: "" });
        return;
      }
      const nameRules = /^[A-Za-z0-9-_ ]+$/;
      const isNameValid = nameRules.test(newName);
      if (!isNameValid) {
        KMainFunctions.displayWarningNotification(
          "Name is invalid. Name should contain only letters, numbers, spaces, underscores and dashes."
        );
        // newName = this.getItemName();
        this.setState({ isRenameLoading: false });
        return;
      }
      this.changeDiFriendlyName(newName);
      this.props.sd.setRenamingDisplayItemId(undefined);
    }
  };

  changeScreenName = async (newName: string) => {
    this.setState({ isRenameLoading: true });
    const screenNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const isScreenNameValid = screenNameRules.test(newName);
    const isEqualToDsName = this.props.sd?.datasources?.find((ds) => ds.name.toLowerCase() === newName.toLowerCase());
    const isEqualToWfName = this.props.sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === newName.toLowerCase());
    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (s) => s.name.toLowerCase() === newName.toLowerCase() && s.id !== this.props.sd?.sdId
    );
    const isEqualToTableName = Array.from(this.props.sd?.schemas.values()).find((schema) =>
      schema?.tables.find((table) => table.name.toLowerCase() === newName.toLowerCase())
    );
    if (!isScreenNameValid) {
      KMainFunctions.displayWarningNotification("Screen Name is invalid");
      newName = this.getItemName(false);
      this.setState({ isRenameLoading: false });
      return;
    }
    if (isEqualToDsName || isEqualToWfName || isEqualToTableName) {
      KMainFunctions.displayWarningNotification(
        "Screen name can not be equal to the datasource, table or process name."
      );
      this.setState({ isRenameLoading: false });
      return;
    }
    if (isEqualToScreenName) {
      KMainFunctions.displayWarningNotification("Screen name already exists");
      this.setState({ isRenameLoading: false });
      return;
    }
    try {
      if (newName?.length > 0) {
        await this.props.sd.saveScreen(false, newName);
        this.props?.sd?.setIsScreenWindowReload(true);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isRenaming: false, renamingId: "", isRenameLoading: false });
    }
  };

  isScreenSelectedForRename = (): boolean => {
    return this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI && this.state.renamingId === this.props.sd.sdId;
  };

  isDisplayItemSelectedForRename = (): boolean => {
    return this.getSelectedDisplayItem()?.id.toString() === this.state.renamingId;
  };

  handleComponentRenamingFocus = (event) => event.target.select();

  isItemRenamable = () => {
    // const di = ScreenDesignHelper.findDisplayItemBykuikaid(
    //   this.props.sd.selectedKuikaIds[0],
    //   this.props.sd.getScreenDesign().displayItems
    // );
    const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
    const isLayoutComponent = this.isLayoutComponent(di);
    if (isLayoutComponent) {
      return false;
    }
    if (di && di.item) {
      const diItem = di.item;
      const compDef = this.props.sd.getComponentDefinition(diItem);
      if (compDef?.isRenamingDisabled === true) return false;
    }
    return true;
  };

  triggerDeleteScreenModal = (e, sd: IScreenDesignContextState) => {
    e.stopPropagation();
    this.setState({ dropdownVisible: false });

    if (this.props.sd.screenList.length === 1) {
      KMainFunctions.displayWarningNotification("You can't delete last screen of project.");
      return;
    }

    this.setState({ triggerScreenDeleteModal: true, screenToDelete: sd });
  };

  async deleteScreen() {
    const designSituation = this.props.sd.dashboardState === DashboardState.design ? "screen" : "report";
    const reportDesignPageToOpen = this.props.sd.screenList?.find(
      (sd) => sd.publicMetaData?.screenType === EScreenType.Report && sd.id !== this.state.screenToDelete.sdId
    );
    const screenToOpen = this.props.sd.screenList?.find(
      (sd) => sd.id !== this.state.screenToDelete.sdId && sd.publicMetaData?.screenType !== EScreenType.Report
    );
    if (designSituation === "screen" && !screenToOpen) {
      KMainFunctions.displayWarningNotification("You can't delete last screen of project.");
      return;
    }
    try {
      await this.props.sd.saveScreen(true);
      if (this.props.sd.sdId === this.state.screenToDelete.sdId) {
        const res = await ScreenService.DeleteCurrentScreen(this.state.screenToDelete.sdId.toString());
        if (res.status === 200) {
          KMainFunctions.displaySuccessNotification(
            `${this.state.screenToDelete.sdName} ${designSituation} has been deleted successfully.`
          );
          this.props?.sd?.setIsScreenWindowReload(true);
          if (designSituation === "report") {
            if (reportDesignPageToOpen) {
              this.props.sd.loadScreen(reportDesignPageToOpen.id);
            } else {
              this.props.sd.loadScreen(screenToOpen?.id);
              this.props.sd.setVisibilityOfCreateReportDesignPage(true);
              this.props.sd.setDashboardState(DashboardState.design);
            }
          } else {
            this.props.sd.loadScreen(screenToOpen?.id);
          }
        }
      } else {
        const res = await ScreenService.Delete(this.state.screenToDelete.sdId.toString());
        if (res.status === 200) {
          KMainFunctions.displaySuccessNotification(
            `${this.state.screenToDelete.sdName} ${designSituation} has been deleted successfully.`
          );
          this.props?.sd?.setIsScreenWindowReload(true);
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  deleteSelectedItem = async () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];

    const interactionDisabledFriendlyNames: string[] =
      ScreenDesignHelper.getFriendlyNamesOfChildDisplayItemsWhichInteractionsDisabled(
        parseInt(sourceKuikaId, 10),
        this.props.sd.screenDesignDictionary
      );

    if (interactionDisabledFriendlyNames.length > 0) {
      KMainFunctions.displayWarningNotification(
        `This element can not be deleted as it contains some elements with disabled interactions. Move these elements to another parent element and try again: ${interactionDisabledFriendlyNames.join(
          ", "
        )}`
      );
      return;
    }

    const message = { kuikaid: sourceKuikaId.toString() };

    this.props.sd.removeItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  copySelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.copyItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  pasteSelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.pasteItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  pasteStylingSelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.pasteItemStyleMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  cloneSelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.cloneItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  getClipboardValue = (): any => {
    return this.props.sd.getClipboardValue();
  };

  updateClipboardValue = (): void => {
    this.clipboardValue = this.getClipboardValue();
  };

  duplicateScreen = async (sd: IScreenDesignContextState) => {
    const accessName = "Screens";
    if (
      KMainFunctions.isDomainHasAccess(
        accessName,
        this.props.sd.screenList.filter((screen) => screen.publicMetaData?.screenType !== EScreenType.Report).length
      ) === false
    ) {
      this.props.showDomainAccessRequiredModal(accessName);
      return;
    }

    this.props.sd.processWithSpinner(async () => {
      try {
        this.setState({ dropdownVisible: false });
        const result = await duplicateDesignerScreen(getSdInstance(), sd.sdId);
        if ("error" in result) {
          throw new Error(result.error);
        }

        if (result.success) {
          KMainFunctions.displaySuccessNotification("Duplicate has been successful.");
          await this.props?.sd?.setIsScreenWindowReload(true);
        }
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      }
    });
  };

  handleDropdownVisibleChange = (visible) => {
    this.setState({ dropdownVisible: visible });
  };

  isLayoutComponent = (node: IDisplayItem | undefined): boolean => {
    const item: IDisplayItemComponent | undefined = node?.item;
    const itemDefinition: IComponentDefinition | undefined = this.props.sd.getComponentDefinition(item);
    if (
      itemDefinition &&
      (itemDefinition.componentType === ComponentType.layoutLeft ||
        itemDefinition.componentType === ComponentType.layoutRight ||
        itemDefinition.componentType === ComponentType.layoutHeader ||
        itemDefinition.componentType === ComponentType.layoutFooter)
    ) {
      return true;
    }
    return false;
  };

  isMicroApp = (node: IDisplayItem | undefined): boolean => {
    const item: IDisplayItemComponent | undefined = node?.item;
    const itemDefinition: IComponentDefinition | undefined = this.props.sd.getComponentDefinition(item);
    if (itemDefinition && itemDefinition.name === "MicroApp") {
      return true;
    }
    return false;
  };

  private getIframeReactHelper = (): any => {
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow) return null;
      return (iframe.contentWindow as any)?.kuika?.reactHelper;
    } catch {
      return null;
    }
  };

  deselectMultiSelectItem = (kuikaId: string): void => {
    const reactHelper = this.getIframeReactHelper();
    const multiSelectManager = reactHelper?.multiSelectManager;
    if (!multiSelectManager) return;

    multiSelectManager.removeSelection(kuikaId);
    const count = multiSelectManager.getSelectedCount();

    if (count <= 1) {
      const remainingId = multiSelectManager.getFirstSelectedKuikaId?.();
      multiSelectManager.clearSelection();
      if (remainingId) {
        this.props.sd.setSelectedDisplayItem(remainingId);
      }
    } else {
      multiSelectManager.highlightSelectedComponents();
    }
    this.setState({ multiSelectCount: count });
  };

  duplicateMultiSelectedItems = (): void => {
    const reactHelper = this.getIframeReactHelper();
    if (reactHelper?.cloneMultiSelectedItems) {
      reactHelper.cloneMultiSelectedItems();
    }
  };

  copyMultiSelectedItems = (): void => {
    const reactHelper = this.getIframeReactHelper();
    if (reactHelper?.copyMultiSelectedItems) {
      reactHelper.copyMultiSelectedItems();
    }
  };

  deleteMultiSelectedItems = (): void => {
    const reactHelper = this.getIframeReactHelper();
    if (reactHelper?.removeMultiSelectedItems) {
      reactHelper.removeMultiSelectedItems();
    }
  };

  getMultiSelectInfo = (): {
    isMultiSelect: boolean;
    count: number;
    components: { kuikaId: string; friendlyName: string; componentName: string }[];
  } | null => {
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow) {
        return null;
      }

      const iframeWindow = iframe.contentWindow as any;
      const multiSelectManager = iframeWindow?.kuika?.reactHelper?.multiSelectManager;

      if (!multiSelectManager) {
        return null;
      }

      const hasSelection = multiSelectManager.hasSelection();
      const count = multiSelectManager.getSelectedCount ? multiSelectManager.getSelectedCount() : 0;
      const components = multiSelectManager.getSelectedComponentInfo
        ? multiSelectManager.getSelectedComponentInfo()
        : [];

      return {
        isMultiSelect: hasSelection && count > 1,
        count,
        components
      };
    } catch (error) {
      return null;
    }
  };

  /** Collapse control pinned to the panel's left edge. Rendered inside the resize handle
   *  where dragging is offered, and standalone where it isn't (Report Designer), so a view
   *  never loses collapse just because it can't be resized. */
  private renderEdgeCollapse = (): JSX.Element => (
    <div className="kuika-right-panel__edge-collapse" onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}>
      <KuikaButton
        data-testid="rightPanelCollapseButton"
        type="secondary"
        size="md"
        isIcon
        aria-label="Collapse panel"
        style={{ boxShadow: "var(--shadow-sm)" }}
        onClick={(e?: React.MouseEvent) => {
          e?.stopPropagation();
          this.props.sd.setRightPanelCollapsed(true);
        }}
      >
        <KuikaSvg
          name="layout-sidebar-left-collapse"
          type="sidebar-icons"
          width={16}
          height={16}
          pointer
          style={{ transform: "scaleX(-1)" }}
        />
      </KuikaButton>
    </div>
  );

  render() {
    let hasValidate;
    const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
    if (di) {
      const targetComponent: IDisplayItemComponent | undefined = di?.item;
      if (targetComponent !== undefined) {
        const targetComponentDefinition = this.props?.sd?.getComponentDefinition(targetComponent);
        hasValidate = targetComponentDefinition?.hasValidate;
      }
    }

    const isSelectedScreenIsReport = this.props.sd.getScreenDesign().isAbsoluteDesign === true;
    const multiSelectInfo = this.getMultiSelectInfo();
    const showMultiSelectMessage =
      multiSelectInfo?.isMultiSelect &&
      (this.props.sd.dashboardState === DashboardState.design ||
        this.props.sd.dashboardState === DashboardState.reportDesigner);

    return (
      <>
        {this.props.sd.getVisibility(ViewPart.RightPanel) === Visibility.Visible && (
          <>
            <DeleteModal
              itemName={this.state.screenToDelete.sdName}
              triggerModal={this.state.triggerScreenDeleteModal}
              deleteItem={() => this.deleteScreen()}
              deleteType={this.props.sd.dashboardState === DashboardState.design ? "Screen" : "Report"}
              closeModal={() => this.setState({ triggerScreenDeleteModal: false })}
            />

            <WorkflowRightPanelTabGate
              isWorkflowPanelActive={this.props.sd.getVisibility(ViewPart.WorkflowRightPanel) === Visibility.Visible}
            >
              <Resizable
                className="kuika_rightPanel"
                size={{ width: this.state.panelWidth, height: "100%" }}
                minWidth={RIGHT_MIN_WIDTH}
                maxWidth={getRightPanelMaxWidth(this.props.sd)}
                enable={{ left: supportsRightPanelResize(this.props.sd.dashboardState) }}
                handleComponent={
                  supportsRightPanelResize(this.props.sd.dashboardState)
                    ? {
                        left: (
                          <div
                            className={`kuika-right-panel__resize-handle ${
                              this.state.isResizing ? "kuika-right-panel__resize-handle--resizing" : ""
                            }`}
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize right panel"
                            tabIndex={0}
                            onKeyDown={this.handleResizeKeyDown}
                            style={{ cursor: this.state.isResizing ? "col-resize" : "ew-resize" }}
                          >
                            {/* Collapse affordance stacked above the resize grip. */}
                            {this.renderEdgeCollapse()}
                            <KuikaButton
                              data-testid="rightPanelResizeGrip"
                              type="secondary"
                              size="md"
                              isIcon
                              style={{ boxShadow: "var(--shadow-sm)" }}
                            >
                              <KuikaSvg name="arrows-horizontal" type="common" width={16} height={16} />
                            </KuikaButton>
                          </div>
                        )
                      }
                    : undefined
                }
                onResizeStart={this.handleResizeStart}
                onResize={this.handleResize}
                onResizeStop={this.handleResizeStop}
              >
                {/* Views that collapse but don't resize can't hang the affordance off a resize
                    handle that re-resizable never renders, so it gets a handle of its own.
                    This replaces the legacy floating chevron that used to serve those views. */}
                {!supportsRightPanelResize(this.props.sd.dashboardState) && (
                  <div className="kuika-right-panel__resize-handle kuika-right-panel__resize-handle--collapse-only">
                    {this.renderEdgeCollapse()}
                  </div>
                )}
                <div
                  style={{
                    // design / reportDesigner: 124px is the old 148px minus the removed 24px
                    // designer footer. KOA-18354 moved the panel BESIDE row-2 instead of under
                    // it, so the sub-bar's height is no longer taken off here — the panel owns
                    // that space now.
                    height:
                      (this.props.sd.dashboardState === DashboardState.reportDesigner && isSelectedScreenIsReport) ||
                      this.props.sd.dashboardState === DashboardState.design
                        ? "calc(100vh - 124px)"
                        : "calc(100vh - 170px)"
                  }}
                >
                  {((this.props.sd.dashboardState === DashboardState.reportDesigner && isSelectedScreenIsReport) ||
                    this.props.sd.dashboardState === DashboardState.design) && <RightPanelHeader />}
                  {!showMultiSelectMessage &&
                    (this.props.sd.dashboardState === DashboardState.design ||
                      (this.props.sd.dashboardState === DashboardState.reportDesigner &&
                        isSelectedScreenIsReport &&
                        !this.props.sd.isReportDesignerLandingVisible())) && (
                      <div className="kuika-flex-center kuika-padding-left-m kuika-padding-right-m kuika-padding-top-s kuika-padding-bottom-l kuika-border-bottom">
                        <KuikaTabs
                          dataTestId="rightPanelTabs"
                          options={
                            this.props.sd.chatDockedToPanel === "right"
                              ? ["Properties", "Styling", "AI Chat"]
                              : ["Properties", "Styling"]
                          }
                          value={
                            this.props.sd.getActiveRightPanel() === RightPanelType.AIChat
                              ? "AI Chat"
                              : this.props.sd.getActiveRightPanel() === RightPanelType.Properties
                                ? "Properties"
                                : "Styling"
                          }
                          onChange={(value) => {
                            if (value === "Properties") {
                              this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
                            } else if (value === "AI Chat") {
                              this.props.sd.setActiveRightPanelByClick(RightPanelType.AIChat);
                            } else {
                              this.props.sd.setActiveRightPanelByClick(RightPanelType.Styling);
                            }
                          }}
                        />
                      </div>
                    )}
                  {this.props.sd.getVisibility(ViewPart.CustomAITemplatePropertiesPanel) === Visibility.Visible && (
                    <CustomAIActionTemplateProperties />
                  )}
                  {/* Scheduled Jobs and the API Builder are intentionally absent. Both now open
                      from a landing table whose row menu already carries this strip's actions
                      (activate/inactivate/delete for a job, delete for an API), and both editors
                      name the thing in their own header — so the strip was a second copy of the
                      name above a second copy of the menu. Bluetooth has no landing page yet. */}
                  {this.props.sd.getVisibility(ViewPart.BluetoothPropertiesPanel) === Visibility.Visible &&
                    this.props.sd.getSelectedBluetoothMethod() && (
                      // ||
                      // (this.props.sd.getVisibility(ViewPart.WorkflowRightPanel) === Visibility.Visible &&
                      //   this.props.sd.selectedWf)
                      <RightPanelItemNameComponent />
                    )}
                  {showMultiSelectMessage ? (
                    <div className="kuika-multiselect-panel">
                      <div className="kuika-multiselect-panel__actions">
                        <KuikaTooltip content="Duplicate selected" position="bottom">
                          <KuikaButton type="secondary" size="sm" onClick={() => this.duplicateMultiSelectedItems()}>
                            <KuikaSvg name="copy-plus" width={14} height={14} />
                            Duplicate
                          </KuikaButton>
                        </KuikaTooltip>
                        <KuikaTooltip content="Copy selected" position="bottom">
                          <KuikaButton type="secondary" size="sm" onClick={() => this.copyMultiSelectedItems()}>
                            <KuikaSvg name="copy" width={14} height={14} />
                            Copy
                          </KuikaButton>
                        </KuikaTooltip>
                        <KuikaTooltip content="Delete selected" position="bottom">
                          <KuikaButton
                            type="secondary"
                            size="sm"
                            color="danger"
                            onClick={() => this.deleteMultiSelectedItems()}
                          >
                            <KuikaSvg name="trash" width={14} height={14} />
                            Delete
                          </KuikaButton>
                        </KuikaTooltip>
                      </div>

                      <div className="kuika-multiselect-panel__list-header">
                        <span className="kuika-multiselect-panel__list-header-title">Components</span>
                        <KuikaBadge content={`${multiSelectInfo?.count || 0}`} color="orange" type="label" />
                      </div>

                      <div className="kuika-multiselect-panel__list">
                        {multiSelectInfo?.components?.map(
                          (comp: { kuikaId: string; friendlyName: string; componentName: string }) => (
                            <div
                              key={comp.kuikaId}
                              className={classNames("kuika-multiselect-panel__item", OVERFLOW_REVEAL_HOST_CLASS)}
                            >
                              <div className="kuika-multiselect-panel__item-info">
                                <KuikaComponentIcon
                                  className="kuika-multiselect-panel__item-icon"
                                  name={comp.componentName}
                                  width={16}
                                  height={16}
                                />
                                <div className="kuika-multiselect-panel__item-text">
                                  <span className="kuika-multiselect-panel__item-name">
                                    <KuikaOverflowText>{comp.friendlyName || comp.componentName}</KuikaOverflowText>
                                  </span>
                                  <span className="kuika-multiselect-panel__item-type">
                                    <KuikaOverflowText>{comp.componentName}</KuikaOverflowText>
                                  </span>
                                </div>
                              </div>
                              <KuikaTooltip content="Deselect" position="left">
                                <button
                                  className="kuika-multiselect-panel__item-deselect"
                                  onClick={() => this.deselectMultiSelectItem(comp.kuikaId)}
                                >
                                  <KuikaSvg name="x" width={12} height={12} />
                                </button>
                              </KuikaTooltip>
                            </div>
                          )
                        )}
                      </div>

                      <div className="kuika-multiselect-panel__hint">
                        <KuikaSvg name="info-circle" width={13} height={13} />
                        <span>
                          {navigator.platform.indexOf("Mac") > -1 ? "Cmd" : "Ctrl"}+Click to add or remove items
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {this.props.sd.dashboardState !== DashboardState.workFlow &&
                        this.props.sd.dashboardState !== DashboardState.zendesk && <PropertiesWindow />}
                      <StylingWindow updateDebounce={250} />
                    </>
                  )}
                  {this.props.sd.getVisibility(ViewPart.DocumentsPanel) === Visibility.Visible && <DocumentsWindow />}
                  {this.props.sd.getVisibility(ViewPart.WorkflowRightPanel) === Visibility.Visible && (
                    <WorkflowWindow />
                  )}
                  {this.props.sd.getVisibility(ViewPart.ZendeskPropertiesPanel) === Visibility.Visible && (
                    <ZendeskProperties />
                  )}
                  {this.props.sd.getVisibility(ViewPart.AIChatRightPanel) === Visibility.Visible && (
                    <div
                      style={{
                        display: this.props.sd.getActiveRightPanel() === RightPanelType.AIChat ? "contents" : "none"
                      }}
                    >
                      <McpChatDocked />
                    </div>
                  )}
                </div>
                {/* resize handle now provided by Resizable.handleComponent.left */}
              </Resizable>
            </WorkflowRightPanelTabGate>
          </>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeDiFriendlyName ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.Loading
    ) {
      return true;
    }
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const designRightPanel = connect(
  null,
  mapDispatchToProps
)(withContext<IDesignRightPanelProps>(withSD<IDesignRightPanelProps>(DesignRightPanel)));
export { designRightPanel as DesignRightPanel };
